import { Group, Radio, Stack, Text, Title, Select, LoadingOverlay } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { SelectPrescriptionPackage } from 'pages/Main/Patients/CreatePrescription/SelectPrescriptionPackage';
import { ActivationType, PrescriptionType } from 'models/prescription';
import { RadioBox } from 'components/RadioBox';
import { useCreatePrescriptionFormContext } from 'context/FormContext';
import { PatientIdProps } from 'models/general';
import { PRESCRIPTION_REASONS, PRESCRIPTION_REASONS_USA } from 'utils/prescription';
import { showUsaFeature } from 'utils/app';
import { usePatient, useUserData } from 'hooks/api/useUser';
import { PrescriptionCompatibleCheck } from './PrescriptionCompatibleCheck';
import { PrescriptionConsentForm } from './PrescriptionConsentForm';

export const FormPrescriptionSelector = ({ patientId }: PatientIdProps) => {
  const { t } = useTranslation();
  const { data: userData, isLoading } = useUserData();
  const { data: patientData } = usePatient({ patientId });
  const { setFieldValue, values, getInputProps, errors } = useCreatePrescriptionFormContext();
  const prescriptionReasons = showUsaFeature ? PRESCRIPTION_REASONS_USA : PRESCRIPTION_REASONS;

  const handleChangePrescriptionType = (prescriptionType: PrescriptionType) => {
    setFieldValue('packageId', '');
    setFieldValue('prescriptionType', prescriptionType);
    if (patientData) {
      setFieldValue(
        'activationType',
        prescriptionType === PrescriptionType.Free ? ActivationType.Immediate : ActivationType.Deferred,
      );
    }
  };

  if (isLoading || !userData) return <LoadingOverlay visible />;

  return (
    <Stack>
      {showUsaFeature && (
        <Stack gap={0}>
          <Title order={4} c={'var(--mantine-color-turquoise-7)'}>
            {t('create_prescription_metadata')}
          </Title>
          <PrescriptionCompatibleCheck
            checked={values.consultedCompatibleDevices}
            onChange={(value) => setFieldValue('consultedCompatibleDevices', value)}
          />
          <PrescriptionConsentForm
            checked={values.usBillingConsent ?? false}
            onCheckedChange={(value) => setFieldValue('usBillingConsent', value)}
            checkedError={errors.usBillingConsent}
            icdCode={values.usBillingIcdCode}
            onIcdCodeChange={(value) => setFieldValue('usBillingIcdCode', value)}
            icdError={errors.usBillingIcdCode}
          />
        </Stack>
      )}
      <Stack gap={'xs'}>
        <Select
          label={t('add_prescript_reason')}
          labelProps={{ 'data-testid': 'input-label-prescription-reason' }}
          data={prescriptionReasons
            .map((value) => ({
              label: t(`${value}_label`),
              value,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))}
          placeholder={t('reason_placeholder')}
          {...getInputProps('reason')}
          data-testid={'input-prescription-reason'}
        />
        {showUsaFeature && (
          <Text fz={'xs'} c={'black'} py={0} my={0}>
            {t('add_prescript_reason_note')}
          </Text>
        )}
      </Stack>

      <Select
        label={t('group_label')}
        labelProps={{ 'data-testid': 'input-label-prescription-group' }}
        data={userData?.visibleGroups.map((group) => ({
          label: group.name,
          value: group.id,
        }))}
        placeholder={t('group_placeholder')}
        {...getInputProps('groupId')}
        onChange={(groupId) => {
          setFieldValue('packageId', '');
          setFieldValue('groupId', groupId || '');
        }}
        data-testid={'input-prescription-group'}
      />

      <Radio.Group
        name={'package'}
        label={t('add_prescript_package')}
        labelProps={{ 'data-testid': 'input-label-prescription-type' }}
        {...getInputProps('prescriptionType')}
        onChange={(value) => handleChangePrescriptionType(value as PrescriptionType)}
      >
        <Group grow>
          {!showUsaFeature && (
            <RadioBox
              key={PrescriptionType.Paid}
              disabled={!values.groupId}
              checked={values.prescriptionType === PrescriptionType.Paid}
              value={PrescriptionType.Paid}
              label={t(`${PrescriptionType.Paid}_label`)}
              data-testid={`radio-box-prescription-${PrescriptionType.Paid}}`}
            />
          )}

          <RadioBox
            key={PrescriptionType.Free}
            disabled={!values.groupId}
            checked={values.prescriptionType === PrescriptionType.Free}
            value={PrescriptionType.Free}
            label={t(`${PrescriptionType.Free}_label`)}
            data-testid={`radio-box-prescription-${PrescriptionType.Free}`}
          />
        </Group>
      </Radio.Group>
      <Text data-testid={`label-prescription-type-description`}>
        {t(`add_prescript_package_${values.prescriptionType}_description`)}
      </Text>
      <SelectPrescriptionPackage patientId={patientId} />
    </Stack>
  );
};
